<template>
  <div class="template-vehicle-panel" v-loading="loading">
    <div class="panel-header">
      <div class="panel-title">区域初始车辆</div>
      <div class="panel-toolbar">
        <el-radio-group v-model="mode" size="mini" @change="onModeChange">
          <el-radio-button label="default">默认配置</el-radio-button>
          <el-radio-button label="date">指定日期</el-radio-button>
        </el-radio-group>
        <el-date-picker v-if="mode === 'date'" v-model="editDate" type="date" value-format="yyyy-MM-dd"
          placeholder="选择日期" size="mini" style="width: 140px" @change="loadConfig"></el-date-picker>
        <el-tag v-if="sourceLabel" size="mini" :type="sourceTagType">{{ sourceLabel }}</el-tag>
        <el-tag v-if="mode === 'date' && !configured" size="mini" type="warning">未单独配置</el-tag>
        <el-button size="mini" type="primary" :loading="saving" :disabled="!dirty" @click="saveConfig">保存</el-button>
        <el-button v-if="mode === 'date' && configured" size="mini" :loading="deleting"
          @click="deleteConfig">删除日期配置</el-button>
      </div>
    </div>

    <p class="panel-desc">
      预售期模板司机各区域初始车辆数，按日期单独入库；指定日期未配置时回落默认配置，默认未配置时使用内置种子。
    </p>

    <div class="configured-dates" v-if="visibleConfiguredDates.length">
      <span class="dates-label">已配置日期：</span>
      <el-tag v-for="item in visibleConfiguredDates" :key="item" size="mini" class="date-tag"
        :effect="mode === 'date' && editDate === item ? 'dark' : 'plain'" @click="selectConfiguredDate(item)">{{ item
        }}</el-tag>
    </div>

    <div class="usable-summary" v-if="usableQueryDate">
      <div class="usable-main">
        <span class="usable-label">{{ usableDateLabel }}总可用车辆：</span>
        <span class="usable-value">{{ usableCount != null ? usableCount : "-" }}</span>
      </div>
      <div class="usable-detail">
        <span>排班司机 {{ scheduledCount != null ? scheduledCount : "-" }}</span>
        <span class="usable-op">−</span>
        <span>当日请假 {{ leaveCount != null ? leaveCount : "-" }}</span>
        <span class="usable-config-total" v-if="configuredVehicleTotal != null">
          · 当前配置合计 {{ configuredVehicleTotal }}
          <el-tag v-if="usableCount != null && configuredVehicleTotal > usableCount" size="mini"
            type="danger">超出可用</el-tag>
        </span>
      </div>
    </div>

    <el-table :data="areaRows" size="mini" border class="compact-table">
      <el-table-column label="区域" width="160">
        <template slot-scope="scope">{{ areaLabel(scope.row.areaId) }}</template>
      </el-table-column>
      <el-table-column label="初始车辆数" width="140">
        <template slot-scope="scope">
          <el-input-number :value="vehiclesValue(scope.row.areaId)" :min="0" :max="999" :step="1" :precision="0"
            :controls="false" size="mini" @change="v => updateVehicles(scope.row.areaId, v)"></el-input-number>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { DateUtil } from "@/util";
import {
  getEvTemplateVehicles,
  saveEvTemplateVehicles,
  deleteEvTemplateVehicles,
  listEvTemplateVehicleDates,
  getEvUsableDrivers
} from "@/api/ev_solver";
import {
  DEFAULT_AREA_IDS,
  SOURCE_LABELS,
  copyNumberKeyMap,
  mergeWithSeed,
  toStringKeyMap
} from "../../utils/evTemplateVehicleDefaults";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      dirty: false,
      mode: "default",
      editDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
      vehiclesByArea: {},
      source: "SEED",
      configured: false,
      configuredDates: [],
      defaultConfigured: false,
      loadSeq: 0,
      usableCount: null,
      scheduledCount: null,
      leaveCount: null,
      usableLoadSeq: 0
    };
  },

  computed: {
    ...mapState({
      areaOpts: (state) => state.areaOpts
    }),

    areaOptions() {
      if (this.areaOpts && this.areaOpts.length) {
        return this.areaOpts;
      }
      return DEFAULT_AREA_IDS.map((id) => ({ id, name: `区域${id}` }));
    },

    areaIds() {
      const fromConfig = Object.keys(this.vehiclesByArea || {}).map(Number);
      return [...new Set([...DEFAULT_AREA_IDS, ...fromConfig])].sort((a, b) => a - b);
    },

    areaRows() {
      return this.areaIds.map((areaId) => ({ areaId }));
    },

    sourceLabel() {
      return SOURCE_LABELS[this.source] || this.source;
    },

    sourceTagType() {
      if (this.source === "DATE") return "success";
      if (this.source === "DEFAULT") return "";
      return "warning";
    },

    queryDate() {
      return this.mode === "default" ? "default" : this.editDate;
    },

    usableQueryDate() {
      if (this.mode === "date" && this.editDate) {
        return this.editDate;
      }
      return DateUtil.getDayStr(0, "yyyy-MM-dd");
    },

    usableDateLabel() {
      const today = DateUtil.getDayStr(0, "yyyy-MM-dd");
      if (this.usableQueryDate === today) {
        return "今日";
      }
      return this.usableQueryDate;
    },

    configuredVehicleTotal() {
      return this.areaIds.reduce((sum, areaId) => sum + this.vehiclesValue(areaId), 0);
    },

    visibleConfiguredDates() {
      const today = DateUtil.getDayStr(0, "yyyy-MM-dd");
      return (this.configuredDates || []).filter((item) => item >= today);
    }
  },

  mounted() {
    this.loadDates().then(() => this.loadConfig());
  },

  methods: {
    areaLabel(areaId) {
      const area = this.areaOptions.find((item) => item.id === areaId);
      return area ? `${area.name}(${areaId})` : `区域${areaId}`;
    },

    vehiclesValue(areaId) {
      return this.vehiclesByArea[areaId] != null ? this.vehiclesByArea[areaId] : 0;
    },

    updateVehicles(areaId, value) {
      if (value == null) return;
      this.$set(this.vehiclesByArea, areaId, value);
      this.dirty = true;
    },

    onModeChange() {
      this.dirty = false;
      this.loadConfig();
      this.loadUsableCount();
    },

    selectConfiguredDate(date) {
      this.mode = "date";
      this.editDate = date;
      this.dirty = false;
      this.loadConfig();
      this.loadUsableCount();
    },

    loadUsableCount() {
      if (!this.usableQueryDate) {
        this.usableCount = null;
        this.scheduledCount = null;
        this.leaveCount = null;
        return Promise.resolve();
      }
      const reqId = ++this.usableLoadSeq;
      return getEvUsableDrivers({ date: this.usableQueryDate, _hideLoading: true })
        .then((res) => {
          if (reqId !== this.usableLoadSeq) return;
          const data = res.data || {};
          this.scheduledCount = data.scheduledCount != null ? data.scheduledCount : null;
          this.leaveCount = data.leaveCount != null ? data.leaveCount : null;
          this.usableCount = data.usableCount != null ? data.usableCount : null;
        })
        .catch(() => {
          if (reqId === this.usableLoadSeq) {
            this.usableCount = null;
            this.scheduledCount = null;
            this.leaveCount = null;
          }
        });
    },

    loadDates() {
      return listEvTemplateVehicleDates({ _hideLoading: true }).then((res) => {
        const data = res.data || {};
        this.configuredDates = data.dates || [];
        this.defaultConfigured = !!data.defaultConfigured;
      });
    },

    loadConfig() {
      if (this.mode === "date" && !this.editDate) {
        return Promise.resolve();
      }
      const reqId = ++this.loadSeq;
      this.loading = true;
      return getEvTemplateVehicles({ date: this.queryDate, _hideLoading: true })
        .then((res) => {
          if (reqId !== this.loadSeq) return;
          const data = res.data || {};
          this.vehiclesByArea = mergeWithSeed(copyNumberKeyMap(data.vehiclesByArea));
          this.source = data.source || "SEED";
          this.configured = !!data.configured;
          this.dirty = false;
        })
        .then(() => this.loadUsableCount())
        .finally(() => {
          if (reqId === this.loadSeq) {
            this.loading = false;
          }
        });
    },

    buildPayload() {
      const vehiclesByArea = {};
      this.areaIds.forEach((areaId) => {
        vehiclesByArea[areaId] = this.vehiclesValue(areaId);
      });
      return {
        date: this.queryDate,
        vehiclesByArea: toStringKeyMap(vehiclesByArea)
      };
    },

    saveConfig() {
      this.saving = true;
      return saveEvTemplateVehicles(this.buildPayload())
        .then((res) => {
          const data = res.data || {};
          this.vehiclesByArea = mergeWithSeed(copyNumberKeyMap(data.vehiclesByArea));
          this.source = data.source || "DATE";
          this.configured = true;
          this.dirty = false;
          this.$message.success("初始车辆配置已保存");
          return this.loadDates();
        })
        .finally(() => {
          this.saving = false;
        });
    },

    deleteConfig() {
      if (!this.editDate) return;
      this.$confirm(`确认删除 ${this.editDate} 的独立配置？删除后将回落默认/种子值。`, "删除配置", {
        type: "warning"
      }).then(() => {
        this.deleting = true;
        return deleteEvTemplateVehicles({ date: this.editDate })
          .then(() => {
            this.$message.success("已删除日期配置");
            return this.loadDates().then(() => this.loadConfig());
          })
          .finally(() => {
            this.deleting = false;
          });
      });
    }
  }
};
</script>

<style lang="less" scoped>
.template-vehicle-panel {
  flex-shrink: 0;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.panel-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.panel-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #909399;
}

.configured-dates {
  margin-bottom: 8px;
}

.dates-label {
  font-size: 12px;
  color: #606266;
  margin-right: 6px;
}

.date-tag {
  margin: 0 6px 4px 0;
  cursor: pointer;
}

.compact-table {
  max-width: 320px;
}

.usable-summary {
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
}

.usable-main {
  margin-bottom: 4px;
}

.usable-label {
  color: #303133;
  font-weight: 600;
}

.usable-value {
  margin-left: 4px;
  font-size: 16px;
  font-weight: 600;
  color: #409eff;
  font-variant-numeric: tabular-nums;
}

.usable-detail {
  font-size: 12px;
  color: #909399;
}

.usable-op {
  margin: 0 4px;
}

.usable-config-total {
  margin-left: 8px;
}
</style>
